import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useNotifications } from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'
import { useState } from 'react'

export type QuoteSettings = Awaited<
  ReturnType<typeof dashboardv20240509Client.getQuoteSettings>
>['data']

type QuoteSettingsUpdate = Partial<
  Parameters<typeof dashboardv20240509Client.putQuoteSettings>[0]['body']
>

type UpdateOptions = {
  onSuccess: () => void
  onError: (error: unknown) => void
}

export const useESignatureSettings = (data?: QuoteSettings) => {
  const { displayNotification } = useNotifications()

  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const mutator = useMutation(dashboardv20240509Client.putQuoteSettings)

  const update = (patch: QuoteSettingsUpdate, options: UpdateOptions) => {
    mutator.mutate(
      {
        body: {
          isSignaturesEnabled: Boolean(data?.isSignaturesEnabled),
          counterSigners: data?.counterSigners,
          isAutoExecuteQuotesEnabled: Boolean(data?.isAutoExecuteQuotesEnabled),
          notifyCounterSignerLast: Boolean(data?.notifyCounterSignerLast),
          // The API preserves `customerPortalAuthRequired` when omitted, so only
          // its own update adds it to the patch.
          ...patch
        }
      },
      options
    )
  }

  const { fields, queries } = useForm<{
    isSignaturesEnabled: boolean
    isCounterSignersEnabled: boolean
    counterSigners?: { id: string; name?: string; email?: string }[]
    notifyCounterSignerLast: boolean
  }>({
    value: {
      isSignaturesEnabled: Boolean(data?.isSignaturesEnabled),
      isCounterSignersEnabled: Boolean(data?.counterSigners?.at(0)),
      counterSigners: data?.counterSigners,
      notifyCounterSignerLast: Boolean(data?.notifyCounterSignerLast)
    },
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'isSignaturesEnabled',
        validation: []
      },
      {
        property: 'isCounterSignersEnabled',
        validation: [required]
      },
      {
        property: 'counterSigners',
        validation: []
      },
      {
        property: 'notifyCounterSignerLast',
        validation: []
      }
    ]
  })

  const updateESignatureEnable = (value: boolean) => {
    update(
      {
        isSignaturesEnabled: value,
        // Preserve the e-signature toggle's existing full-replace request shape.
        counterSigners: undefined,
        notifyCounterSignerLast: false,
        /**
         * Always disable auto-execute when e-signatures are toggled so quote start date is always optional in the
         * editor when e-signatures are disabled and therefore auto-execute is unavailable
         */
        isAutoExecuteQuotesEnabled: false
      },
      {
        onSuccess: () => {
          displayNotification(`E-signature updated`, {
            type: 'success'
          })
          setShowValidationErrors(false)
        },
        onError: () => {
          displayNotification(`E-signature update failed`, {
            type: 'error'
          })
        }
      }
    )
  }

  const updateIsAutoExecuteQuotesEnabled = (value: boolean) => {
    update(
      {
        isAutoExecuteQuotesEnabled: value
      },
      {
        onSuccess: () => {
          displayNotification('Auto execute accepted quotes setting updated', {
            type: 'success'
          })
          setShowValidationErrors(false)
        },
        onError: () => {
          displayNotification(
            'Failed to update auto execute accepted quotes setting',
            { type: 'error' }
          )
        }
      }
    )
  }

  const updateNotifyCounterSignerLast = (value: boolean) => {
    update(
      {
        notifyCounterSignerLast: value
      },
      {
        onSuccess: () => {
          displayNotification('Notify countersigner last setting updated', {
            type: 'success'
          })
          setShowValidationErrors(false)
        },
        onError: () => {
          displayNotification(
            'Failed to update notify countersigner last setting',
            { type: 'error' }
          )
        }
      }
    )
  }

  const mutateCountersigner = ({
    enable = true,
    updatedCounterSigners = []
  }: {
    enable?: boolean
    updatedCounterSigners?: { id?: string; name?: string; email?: string }[]
  }) => {
    setShowValidationErrors(true)

    const counterSigners = enable ? updatedCounterSigners : []

    update(
      {
        counterSigners,
        notifyCounterSignerLast: enable
          ? Boolean(data?.notifyCounterSignerLast)
          : false
      },
      {
        onSuccess: () => {
          displayNotification('Countersigner updated', { type: 'success' })
          setShowValidationErrors(false)
        },
        onError: (error: any) => {
          displayNotification('Countersigner update failed', {
            type: 'error',
            description: error?.response?.error?.errors.at(0)?.message
          })
        }
      }
    )
  }

  const addCounterSigner = () => {
    const newPerson = { id: 'new', name: undefined, email: undefined }
    const newCounterSigners = queries.formData.counterSigners
      ? [...queries.formData.counterSigners, newPerson]
      : [newPerson]

    fields.counterSigners.onChange(newCounterSigners)
    setShowValidationErrors(false)
  }

  const removeCounterSigner = (id: string) => {
    const updatedCounterSigners = queries.formData.counterSigners?.filter(
      p => p.id !== id
    )

    fields.counterSigners.onChange(updatedCounterSigners)
    mutateCountersigner({ updatedCounterSigners })
  }

  const updateCounterSigner = () => {
    if (!queries.isDirty) return

    const updatedCounterSigners = fields.counterSigners.value
      ? fields.counterSigners.value.map(counterSigner =>
          counterSigner.id === 'new'
            ? { name: counterSigner.name, email: counterSigner.email }
            : counterSigner
        )
      : []

    mutateCountersigner({ updatedCounterSigners })
  }

  const clearCounterSigners = () => {
    fields.counterSigners.onChange([])
    mutateCountersigner({ enable: false })
  }

  const updateCustomerPortalAuthRequired = (value: boolean) => {
    update(
      {
        customerPortalAuthRequired: value ? 'REQUIRED' : 'NOT_REQUIRED'
      },
      {
        onSuccess: () => {
          displayNotification('Quote authentication setting updated', {
            type: 'success'
          })
        },
        onError: () => {
          displayNotification('Failed to update quote authentication setting', {
            type: 'error'
          })
        }
      }
    )
  }

  return {
    queries,
    fields,
    isPending: mutator.isPending,
    updateESignatureEnable,
    removeCounterSigner,
    addCounterSigner,
    updateCounterSigner,
    clearCounterSigners,
    showValidationErrors,
    setShowValidationErrors,
    updateIsAutoExecuteQuotesEnabled,
    updateNotifyCounterSignerLast,
    updateCustomerPortalAuthRequired
  }
}
